import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import SideNav from './SideNav'
import TopRootDiv from './TopRootDiv';
import SherDineNav from './SherDineNav';
import { DBContext } from './DBContext';
import './css/Inventory.css'
import { inventoryData } from './InventoryPages/inventoryData';
import {
    InventoryDashboard,
    ItemDetailsPage,
    LowStockAlertPage,
    InventoryReportPage,
    ConsumptionEntryPage,
    ConversionPage,
    MasterSetupPage,
    PurchaseEntryPage,
    PurchaseWorkflowPage,
    RecipeManagementPage,
    ReportsAnalyticsPage,
    StockListPage,
    StockTransferPage,
    SupplierManagementPage,
    WastageEntryPage,
} from './InventoryPages';
import DailyProduction from './InventoryPages/DailyProduction';
import TrendsPage from './InventoryPages/TrendsPage';



const extraPageTitles = {
    'item-details': 'Item Details',
    'stock-summary': 'Stock Summary',
    'stock-valuation': 'Stock Valuation',
    'raw-material-report': 'Raw Material Report',
    'consumption': 'Consumption Report',
    'opening-closing': 'Opening / Closing Stock',
    'purchase': 'Purchase Report',
    'sales-report': 'Sales Report',
    'wastage-report': 'Wastage Report',
    'supplier': 'Supplier Report',
    'internal-transfer': 'Internal Transfer Report',
    'item-wise-internal-transfer': 'Item-wise Transfer Report',
    'manual-adjustment-report': 'Manual Adjustment Report',
    'food-costing': 'Food Costing Report',
    'recipe-cost-report': 'Recipe Cost Report',
    'orderwise-consumption': 'Orderwise Consumption',
    'purchase-order-variance': 'Purchase Order Variance',
    'monthly': 'Monthly Report',
    'purchase-order-approval-report': 'PO Approval Report',
    'purchase-indent': 'Purchase Indent',
    'purchase-order': 'Purchase Order',
    grn: 'GRN',
    'stock-purchase': 'Stock Purchase',
    'purchase-return': 'Purchase Return',
    'consumption-entry': 'Consumption Entry',
    conversion: 'Conversion',
    'production-conversion': 'Production Conversion',
    'yield-management': 'Yield Management',
    'raw-materials': 'Raw Materials',
    'item-recipes': 'Item Recipes',
    suppliers: 'Suppliers',
    units: 'Units',
    'suppliers-hub': 'Suppliers Hub',
    'inventory-approval-flow': 'Inventory Approval Flow',
    'email-template-setting': 'Email Template Setting',
    'inventory-settings': 'Inventory Settings',
};

const pageTitles = {
    dashboard: 'Dashboard',
    'stock-list': 'Stock List',
    'purchase-entry': 'Purchase Entry',
    'low-stock-alerts': 'Low Stock',
    'supplier-management': 'Suppliers',
    'recipe-management': 'Recipes',
    'stock-transfer': 'Transfer',
    'wastage-entry': 'Wastage',
    'reports-analytics': 'Reports',
    'daily-production': 'Daily Production',
    'trends': 'Trends',
    ...extraPageTitles,
};

const aliases = {
    stock: 'stock-list',
    wastage: 'wastage-entry',
    'low-stock-report': 'low-stock-alerts',
};

function Inventory() {
    const navigate = useNavigate();
    const { page = 'dashboard' } = useParams();
    const activePage = aliases[page] || page;
    const title = pageTitles[activePage] || pageTitles.dashboard;
    const { setInventoryStateFunc } = useContext(DBContext);
    const [sideNav, setSideNav] = useState(false);
    const [falseState, setFalseState] = useState(null);

    useEffect(() => {
        setInventoryStateFunc(true);
    }, [setInventoryStateFunc]);

    const stats = useMemo(() => {
        const totalItems = inventoryData.items.reduce((total, item) => total + Number(item.currentStock || 0), 0);
        const lowStock = inventoryData.items.filter(item => item.currentStock > 0 && item.currentStock <= item.threshold).length;
        const outOfStock = inventoryData.items.filter(item => item.currentStock <= 0).length;

        return {
            totalItems,
            lowStock,
            outOfStock,
            purchaseOrders: 4,
        };
    }, []);

    const reportPage = (key) => (
        <InventoryReportPage title={pageTitles[key]} type={key} data={inventoryData} />
    );

    const purchasePage = (key, action) => (
        <PurchaseWorkflowPage title={pageTitles[key]} action={action} items={inventoryData.items} suppliers={inventoryData.suppliers} />
    );

    const masterRows = {
        'raw-materials': inventoryData.items.filter(item => item.category === 'Raw').map(item => ({ id: item.id, name: item.name, type: item.type, status: item.status })),
        'item-recipes': inventoryData.recipes.map((item, index) => ({ id: index + 1, name: item.name, type: `${item.ingredients.length} Ingredients`, status: item.autoDeduct ? 'Auto Deduct' : 'Manual' })),
        suppliers: inventoryData.suppliers.map(item => ({ id: item.id, name: item.name, type: item.contact, status: item.lastOrder })),
        units: ['Kg', 'Box', 'Pcs', 'Ltr', 'Gm'].map((unit, index) => ({ id: index + 1, name: unit, type: 'Inventory Unit', status: 'Active' })),
        'suppliers-hub': inventoryData.suppliers.map(item => ({ id: item.id, name: item.name, type: `Pending ${item.pending}`, status: item.lastOrder })),
        'inventory-approval-flow': ['Purchase Approval', 'Transfer Approval', 'Wastage Approval'].map((name, index) => ({ id: index + 1, name, type: 'Workflow', status: 'Active' })),
        'email-template-setting': ['Low Stock Alert', 'Purchase Order', 'Supplier Reminder'].map((name, index) => ({ id: index + 1, name, type: 'Email Template', status: 'Active' })),
        'inventory-settings': ['Stock Threshold', 'Auto Deduction', 'Approval Required'].map((name, index) => ({ id: index + 1, name, type: 'Setting', status: 'Enabled' })),
    };

    const pageComponents = {
        dashboard: <InventoryDashboard data={inventoryData} stats={stats} navigate={navigate} />,
        'stock-list': <StockListPage items={inventoryData.items} navigate={navigate} />,
        'item-details': <ItemDetailsPage item={inventoryData.items[0]} />,
        'purchase-entry': <PurchaseEntryPage items={inventoryData.items} suppliers={inventoryData.suppliers} />,
        'low-stock-alerts': <LowStockAlertPage items={inventoryData.items} />,
        'supplier-management': <SupplierManagementPage suppliers={inventoryData.suppliers} />,
        'recipe-management': <RecipeManagementPage recipes={inventoryData.recipes} />,
        'stock-transfer': <StockTransferPage items={inventoryData.items} />,
        'wastage-entry': <WastageEntryPage items={inventoryData.items} />,
        'reports-analytics': <ReportsAnalyticsPage />,
        'stock-summary': reportPage('stock-summary'),
        'stock-valuation': reportPage('stock-valuation'),
        'raw-material-report': reportPage('raw-material-report'),
        consumption: reportPage('consumption'),
        'opening-closing': reportPage('opening-closing'),
        purchase: reportPage('purchase'),
        'sales-report': reportPage('sales-report'),
        'wastage-report': reportPage('wastage-report'),
        supplier: reportPage('supplier'),
        'internal-transfer': reportPage('internal-transfer'),
        'item-wise-internal-transfer': reportPage('item-wise-internal-transfer'),
        'manual-adjustment-report': reportPage('manual-adjustment-report'),
        'food-costing': reportPage('food-costing'),
        'recipe-cost-report': reportPage('recipe-cost-report'),
        'orderwise-consumption': reportPage('orderwise-consumption'),
        'purchase-order-variance': reportPage('purchase-order-variance'),
        monthly: reportPage('monthly'),
        'purchase-order-approval-report': reportPage('purchase-order-approval-report'),
        'purchase-indent': purchasePage('purchase-indent', 'Save Indent'),
        'purchase-order': purchasePage('purchase-order', 'Save Purchase Order'),
        grn: purchasePage('grn', 'Save GRN'),
        'stock-purchase': purchasePage('stock-purchase', 'Save Stock Purchase'),
        'purchase-return': purchasePage('purchase-return', 'Save Return'),
        'consumption-entry': <ConsumptionEntryPage items={inventoryData.items} />,
        conversion: <ConversionPage title={pageTitles.conversion} items={inventoryData.items} />,
        'production-conversion': <ConversionPage title={pageTitles['production-conversion']} items={inventoryData.items} />,
        'yield-management': <ConversionPage title={pageTitles['yield-management']} items={inventoryData.items} />,
        'raw-materials': <MasterSetupPage title={pageTitles['raw-materials']} rows={masterRows['raw-materials']} />,
        'item-recipes': <MasterSetupPage title={pageTitles['item-recipes']} rows={masterRows['item-recipes']} />,
        suppliers: <MasterSetupPage title={pageTitles.suppliers} rows={masterRows.suppliers} />,
        units: <MasterSetupPage title={pageTitles.units} rows={masterRows.units} />,
        'suppliers-hub': <MasterSetupPage title={pageTitles['suppliers-hub']} rows={masterRows['suppliers-hub']} />,
        'inventory-approval-flow': <MasterSetupPage title={pageTitles['inventory-approval-flow']} rows={masterRows['inventory-approval-flow']} />,
        'email-template-setting': <MasterSetupPage title={pageTitles['email-template-setting']} rows={masterRows['email-template-setting']} />,
        'inventory-settings': <MasterSetupPage title={pageTitles['inventory-settings']} rows={masterRows['inventory-settings']} />,
        'daily-production': <DailyProduction />,
        'trends': <TrendsPage />,
    };

    return (
        <div className='TableRegMainDiv'>
            <SherDineNav sideNav={(state) => setSideNav(state)} falseState={falseState} />
            <div className='w-full bg-[#f5f5f5] flex gap-3 h-full overflow-hidden'>
                <div className='w-max h-full flex z-[9999]'>
                    <SideNav focus={title} state={sideNav} falseState={(state) => setFalseState(state)} />
                </div>
                <div className='w-full h-full flex flex-col pr-3 pl-0 pb-3 pt-0 gap-3 relative'>
                    <div className='pr-3 h-max w-full bg-[#fff] borderBottom'>
                        <TopRootDiv image='Inventory' root={['Inventory']} section={title} />
                    </div>
                    <main className='inventory-page w-full h-full flex flex-col gap-3 font-nunito overflow-hidden'>
                        {pageComponents[activePage] || pageComponents.dashboard}
                    </main>
                </div>
            </div>
        </div>
    )
}

export default Inventory
